import { Link } from "react-router-dom";
import { useState } from "react";
import { ShieldCheck, ScrollText, Sparkles, FileWarning, Send, Check, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { http } from "../lib/api";

function PublisherOptOutForm() {
  const [form, setForm] = useState({ publisher_name: "", publisher_domain: "", contact_name: "", contact_title: "", contact_email: "", reason: "", signature: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await http.post("/legal/publisher-opt-out", form);
      setSubmitted(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not submit. Please email legal@newseze.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 p-4 text-[13px] text-emerald-800" data-testid="optout-success">
        <Check size={14} className="inline -mt-0.5 mr-1" />
        <b>Opt-out recorded.</b> Your domain will stop being ingested within 1 hour. We&apos;ll send a confirmation to your email.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="optout-form">
      <input required value={form.publisher_name} onChange={(e) => setForm({ ...form, publisher_name: e.target.value })} placeholder="Publisher name (e.g., The Daily Herald)" className="border border-[var(--line)] rounded px-3 py-2 text-[13px]" data-testid="optout-pub-name" />
      <input required value={form.publisher_domain} onChange={(e) => setForm({ ...form, publisher_domain: e.target.value })} placeholder="Publisher domain (e.g., dailyherald.com)" className="border border-[var(--line)] rounded px-3 py-2 text-[13px]" data-testid="optout-pub-domain" />
      <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Your name" className="border border-[var(--line)] rounded px-3 py-2 text-[13px]" data-testid="optout-contact-name" />
      <input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })} placeholder="Your title (optional)" className="border border-[var(--line)] rounded px-3 py-2 text-[13px]" data-testid="optout-contact-title" />
      <input required type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="Your email" className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="optout-email" />
      <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (optional)" rows={2} className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="optout-reason" />
      <input required value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} placeholder="Electronic signature (type your full name)" className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="optout-signature" />
      {err && <div className="text-[12px] text-red-700 sm:col-span-2">{err}</div>}
      <button type="submit" disabled={submitting} className="btn primary sm:col-span-2 justify-center" data-testid="optout-submit">
        {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting&hellip;</> : <><Send size={14} /> Submit opt-out request</>}
      </button>
    </form>
  );
}

function DMCAForm() {
  const [form, setForm] = useState({ name: "", email: "", work_title: "", newseze_url: "", reason: "", signature: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await http.post("/legal/dmca", { ...form, kind: "dmca-takedown" });
      setSubmitted(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not submit. Please email legal@newseze.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 p-4 text-[13px] text-emerald-800" data-testid="dmca-success">
        <Check size={14} className="inline -mt-0.5 mr-1" />
        <b>Notice received.</b> We&apos;ll review and act within 48 hours. You&apos;ll get a confirmation at the email you provided.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dmca-form">
      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className="border border-[var(--line)] rounded px-3 py-2 text-[13px]" data-testid="dmca-name" />
      <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border border-[var(--line)] rounded px-3 py-2 text-[13px]" data-testid="dmca-email" />
      <input required value={form.work_title} onChange={(e) => setForm({ ...form, work_title: e.target.value })} placeholder="Title of the copyrighted work" className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="dmca-work" />
      <input required type="url" value={form.newseze_url} onChange={(e) => setForm({ ...form, newseze_url: e.target.value })} placeholder="Newseze URL where the work appears" className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="dmca-url" />
      <textarea required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for takedown (good-faith statement)" rows={3} className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="dmca-reason" />
      <input required value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} placeholder="Electronic signature (type your full name)" className="border border-[var(--line)] rounded px-3 py-2 text-[13px] sm:col-span-2" data-testid="dmca-signature" />
      {err && <div className="text-[12px] text-red-700 sm:col-span-2">{err}</div>}
      <button type="submit" disabled={submitting} className="btn primary sm:col-span-2 justify-center" data-testid="dmca-submit">
        {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting&hellip;</> : <><Send size={14} /> Submit takedown notice</>}
      </button>
    </form>
  );
}

// Compliance & legal landing page. Linked from Footer.
// Includes: DMCA agent, fair-use posture, AI disclosure, takedown form.
export default function Legal() {
  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[920px] mx-auto px-5 py-10" data-testid="legal-page">
        <div className="section-title">
          <span className="pip" />
          <h2>Legal · Compliance · Policy</h2>
        </div>
        <p className="text-[14px] text-[var(--muted)] mt-2 max-w-[720px]">
          Newseze operates as a fair-use news aggregator. The notes below describe how we
          source, attribute, and respect copyright across every story we surface.
        </p>

        <section className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="legal-fair-use">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3">
            <ScrollText size={13} /> Editorial · Fair Use Posture
          </div>
          <ul className="space-y-2.5 text-[13.5px] text-[var(--ink)] leading-snug list-disc pl-5">
            <li><b>Headline rewrite + 1-sentence brief</b>: short, transformative summaries — never a verbatim reproduction of the publisher&apos;s body text.</li>
            <li><b>Source attribution</b>: every card shows the original publisher and links to the source article. We do not host or mirror publisher body text.</li>
            <li><b>Newseze Analysis</b>: 350–500 word original commentary written by Newseze&apos;s AI staff analyst, citing the publisher as the original reporter. Marked as &ldquo;AI-assisted analysis&rdquo; per FTC guidance.</li>
            <li><b>RSS-only ingestion</b>: every feed is published by the publisher themselves as RSS — an explicit, machine-readable invitation to aggregate titles + summaries.</li>
            <li><b>Iframe behavior</b>: when a publisher sets <code className="bg-[var(--bg)] px-1 rounded">X-Frame-Options: DENY</code>, we do not bypass it — we open the publisher page in a new tab so they receive the full traffic.</li>
            <li><b>No scraping of body content</b>: Newseze never extracts full article text. The full read always happens on the publisher&apos;s own page.</li>
          </ul>
        </section>

        <section className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="legal-ai-disclosure">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3">
            <Sparkles size={13} /> AI Disclosure
          </div>
          <p className="text-[13.5px] text-[var(--ink)] leading-snug">
            Newseze uses Anthropic Claude Sonnet 4.5 to (1) rewrite headlines into a calm,
            respectful register, (2) score each story for evidence/balance/bias/respect, and
            (3) generate the long-form &ldquo;Newseze Analysis&rdquo; on each article page. AI output is
            reviewed against editorial guidelines (no demeaning of public officials; no
            anti-Trump activist framing on the front page; no hard-left or hard-right
            framing). Readers may flag any AI-generated content via the contact form below.
          </p>
        </section>

        <section className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="legal-dmca">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3">
            <FileWarning size={13} /> DMCA · Takedown
          </div>
          <p className="text-[13.5px] text-[var(--ink)] leading-snug mb-3">
            If you own copyright in a work you believe is being misused on Newseze, send a
            DMCA notice to our designated agent. We will act within 48 hours.
          </p>
          <div className="bg-[var(--bg)] rounded-md p-3 text-[12.5px] text-[var(--ink)]">
            <div className="font-black">Designated DMCA Agent</div>
            <div>Newseze Compliance · <a href="mailto:legal@newseze.com" className="text-[var(--blue)] font-black underline">legal@newseze.com</a></div>
          </div>
          <p className="text-[12.5px] text-[var(--muted)] mt-3 italic">
            Required notice contents: identification of the work, the Newseze URL, your contact
            info, a good-faith statement, and a signature (electronic acceptable).
          </p>
          <DMCAForm />
        </section>

        <section className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="legal-publisher-optout">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3">
            <ShieldCheck size={13} /> Publisher Opt-Out
          </div>
          <p className="text-[13.5px] text-[var(--ink)] leading-snug mb-2">
            If you&apos;re a publisher and would prefer Newseze not ingest your RSS feed, fill out
            this form. No copyright claim required &mdash; this is a courtesy mechanism that
            most aggregators don&apos;t offer. Your domain will be added to our deny list and
            skipped on the next ingest cycle (within 1 hour).
          </p>
          <p className="text-[12px] text-[var(--muted)] italic mb-1">
            We respect publisher preferences proactively, even though our RSS-based ingestion
            is a fair use under <a href="https://www.copyright.gov/fair-use/" target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="text-[var(--blue)] font-black underline">17 U.S.C. § 107</a>.
          </p>
          <PublisherOptOutForm />
        </section>

        <section className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="legal-protocols">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3">
            <ShieldCheck size={13} /> Web Protocol Standards
          </div>
          <ul className="space-y-2 text-[13.5px] text-[var(--ink)] list-disc pl-5">
            <li>All external links carry <code className="bg-[var(--bg)] px-1 rounded">rel=&quot;noopener noreferrer&quot;</code> and open in a new tab.</li>
            <li>The Newseze bookmarklet only loads when the reader clicks it — never silently auto-injects.</li>
            <li>We respect <code className="bg-[var(--bg)] px-1 rounded">robots.txt</code> and never crawl pages a publisher disallows.</li>
            <li>Reader emails and phone numbers are stored only with explicit subscribe consent. Data export and deletion are one-click in <Link to="/contact" className="text-[var(--blue)] font-black underline">Contact</Link>.</li>
          </ul>
        </section>
      </main>
      <Footer />
    </>
  );
}
